import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const LOGO_URL = "/assets/lotus-logo.png";

const Header = ({ onOpenQuiz, onOpenBooking }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isOverHero = !isScrolled && location.pathname === "/";
  const navText = isOverHero ? "text-white/90 hover:text-[#DCC286]" : "text-[#4A5543] hover:text-[#C5A059]";
  const logoTitle = isOverHero ? "text-white" : "text-[#2C3524]";
  const logoSub = isOverHero ? "text-white/70" : "text-[#4A5543]";

  const scrollToSection = (sectionId) => {
    if (location.pathname !== "/") {
      window.location.href = `/#${sectionId}`;
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <nav className="flex items-center justify-between h-32 md:h-36">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-3 group"
          >
            <img
              src={LOGO_URL}
              alt="Ivana Silva Coaching"
              className={`h-32 md:h-36 w-auto ${isOverHero ? "" : "lotus-icon"}`}
              style={
                isOverHero
                  ? {
                      filter:
                        "brightness(0) invert(1) drop-shadow(0 1px 3px rgba(0,0,0,0.35))",
                    }
                  : undefined
              }
            />
            <div className="hidden sm:block">
              <span className={`font-heading text-2xl tracking-tight transition-colors ${logoTitle}`}>
                Ivana Silva
              </span>
              <span className={`block text-xs tracking-[0.2em] uppercase transition-colors ${logoSub}`}>
                {t("nav.coaching")}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <button
              onClick={() => scrollToSection("method")}
              className={`font-light transition-colors ${navText}`}
              data-testid="nav-method"
            >
              {t("nav.method")}
            </button>
            <button
              onClick={() => scrollToSection("programs")}
              className={`font-light transition-colors ${navText}`}
              data-testid="nav-programs"
            >
              {t("nav.programs")}
            </button>
            <button
              onClick={() => scrollToSection("about")}
              className={`font-light transition-colors ${navText}`}
              data-testid="nav-about"
            >
              {t("nav.about")}
            </button>
            <button
              onClick={() => scrollToSection("retreats")}
              className={`font-light transition-colors ${navText}`}
              data-testid="nav-retreats"
            >
              {t("nav.retreats")}
            </button>
            <Link
              to="/decode-guide"
              className={`font-light transition-colors ${navText}`}
              data-testid="nav-freebie"
            >
              {t("nav.freebie")}
            </Link>
            <button
              onClick={() => navigate("/quizzes")}
              className="btn-outline text-sm px-6 py-2"
              data-testid="nav-quiz-btn"
            >
              {t("nav.takeQuiz")}
            </button>
            <LanguageSwitcher />
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 transition-colors ${isOverHero ? "text-white" : "text-[#2C3524]"}`}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            className="md:hidden absolute top-32 md:top-36 left-0 right-0 bg-[#F9F8F6] border-b border-[#E3DAC9] shadow-lg animate-fade-in"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col p-6 gap-4">
              <button
                onClick={() => scrollToSection("method")}
                className="text-left text-[#4A5543] hover:text-[#C5A059] py-2"
              >
                {t("nav.method")}
              </button>
              <button
                onClick={() => scrollToSection("programs")}
                className="text-left text-[#4A5543] hover:text-[#C5A059] py-2"
              >
                {t("nav.programs")}
              </button>
              <button
                onClick={() => scrollToSection("about")}
                className="text-left text-[#4A5543] hover:text-[#C5A059] py-2"
              >
                {t("nav.about")}
              </button>
              <button
                onClick={() => scrollToSection("retreats")}
                className="text-left text-[#4A5543] hover:text-[#C5A059] py-2"
              >
                {t("nav.retreats")}
              </button>
              <Link
                to="/decode-guide"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-left text-[#4A5543] hover:text-[#C5A059] py-2"
              >
                {t("nav.freebie")}
              </Link>
              <button
                onClick={() => {
                  navigate("/quizzes");
                  setIsMobileMenuOpen(false);
                }}
                className="btn-outline w-full mt-2"
              >
                {t("nav.takeQuiz")}
              </button>
              <div className="pt-2 flex justify-center">
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
